<app-breadcrumb
  [title]="'invoices_v2.view.view_invoice' | translate"
  [showTrail]="false"
>
</app-breadcrumb>

<!-- Main Container -->
<div class="invoice-view-container" *ngIf="invoice">
  <div class="invoice-layout">
    <!-- Left Panel - Info & Actions -->
    <div class="info-panel">
      <!-- Back Button -->
      <button class="back-btn" (click)="goBack()">
        <i class="fa fa-arrow-left"></i>
        <span>{{ 'invoices_v2.view.back_to_list' | translate }}</span>
      </button>

      <!-- Invoice List Selection Card -->
      <div class="actions-card invoices-list-card">
        <div class="card-title-row">
          <h3 class="card-title" style="margin-bottom: 0;">
            <i class="fa fa-list"></i>
            {{ 'invoices_v2.view.invoices_list' | translate }}
          </h3>
          <button class="toggle-list-btn" (click)="toggleInvoiceList()">
            <i [class]="showInvoiceList ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
          </button>
        </div>

        <!-- Invoice List Content -->
        <div class="invoices-list-content" *ngIf="showInvoiceList">
          <!-- Download Merged PDF Button -->
          <div class="merge-pdf-section" *ngIf="selectedInvoiceIds.length > 0">
            <span class="selected-count">
              {{ 'invoices_v2.view.selected' | translate }}
              {{ selectedInvoiceIds.length }}
            </span>
            <button class="merge-pdf-btn" (click)="downloadMergedPdf()">
              <i class="fa fa-file-pdf-o"></i>
              {{ 'invoices_v2.view.download_merged_pdf' | translate }}
            </button>
          </div>

          <!-- Loading -->
          <div *ngIf="isLoadingList" class="list-loading">
            <div class="loader-small"></div>
          </div>

          <!-- Invoice Items -->
          <div class="invoices-list-items" *ngIf="!isLoadingList">
            <!-- Select All -->
            <div class="invoice-list-item select-all-item" (click)="toggleSelectAll()">
              <input 
                type="checkbox" 
                [checked]="selectedInvoiceIds.length === invoicesList.length && invoicesList.length > 0"
                class="invoice-checkbox"
              >
              <span class="select-all-label">
                {{ 'invoices_v2.view.select_all' | translate }}
              </span>
            </div>

            <!-- Invoice Items -->
            <div 
              *ngFor="let inv of invoicesList" 
              class="invoice-list-item"
              [class.active]="inv.invoice_id === invoiceId"
              [class.selected]="isInvoiceSelected(inv.invoice_id)"
            >
              <input 
                type="checkbox" 
                [checked]="isInvoiceSelected(inv.invoice_id)"
                (click)="$event.stopPropagation(); toggleInvoiceSelection(inv.invoice_id)"
                class="invoice-checkbox"
              >
              <div class="invoice-item-info" (click)="viewSelectedInvoice(inv.invoice_id)">
                <span class="invoice-item-number">{{ inv.document_number || '#' + inv.invoice_id }}</span>
                <span class="invoice-item-date">{{ inv.invoice_date | date: 'dd/MM/yyyy' }}</span>
                <span class="invoice-item-amount">{{ getDisplayAmount(inv) | number:'1.2-2' }} ₪</span>
              </div>
            </div>

            <!-- Empty State -->
            <div *ngIf="invoicesList.length === 0" class="empty-list">
              {{ 'invoices_v2.list.no_invoices' | translate }}
            </div>
          </div>
        </div>
      </div>

      <!-- Status Card -->
      <div class="status-card" [ngClass]="getStatusClass()">
        <div class="status-icon">
          <i [class]="getStatusIcon()"></i>
        </div>
        <div class="status-info">
          <span class="status-label">{{ 'invoices_v2.common.status' | translate }}</span>
          <span class="status-value">{{ getStatusLabel() }}</span>
        </div>
      </div>

      <!-- Invoice Details Card -->
      <div class="details-card">
        <h3 class="card-title">
          <i class="fa fa-file-text-o"></i>
          {{ 'invoices_v2.view.invoice_details' | translate }}
        </h3>
        
        <div class="detail-item">
          <span class="detail-label">
            <i class="fa fa-hashtag"></i>
            {{ 'invoices_v2.view.document_number' | translate }}
          </span>
          <span class="detail-value highlight">{{ invoice.document_number || '---' }}</span>
        </div>

        <div class="detail-item" *ngIf="!isReceipt()">
          <span class="detail-label">
            <i class="fa fa-tag"></i>
            {{ 'invoices_v2.common.invoice_type' | translate }}
          </span>
          <span class="detail-value">{{ getInvoiceTypeLabel(invoice.invoice_type) }}</span>
        </div>

        <div class="detail-item">
          <span class="detail-label">
            <i class="fa fa-calendar"></i>
            {{ 'invoices_v2.common.document_date' | translate }}
          </span>
          <span class="detail-value">{{ invoice.invoice_date | date: 'dd/MM/yyyy' }}</span>
        </div>

        <div class="detail-item" *ngIf="isPaymentTrackingInvoice()">
          <span class="detail-label">
            <i class="fa fa-calendar-check-o"></i>
            {{ 'invoices_v2.payment.due_date' | translate }}
          </span>
          <span class="detail-value detail-value--editable" (click)="$event.stopPropagation()">
            <ng-container *ngIf="canEditPayment(); else dueDateViewReadOnly">
              <button
                type="button"
                class="payment-field-trigger payment-field-trigger--due-date"
                [class.payment-field-trigger--active]="showDueDateEditor"
                [disabled]="isSavingPaymentUpdate"
                (click)="startDueDateEdit($event)"
              >
                <span>{{
                  invoice.due_date
                    ? (invoice.due_date | date: 'dd/MM/yyyy')
                    : ('invoices_v2.payment.add_due_date_short' | translate)
                }}</span>
                <i class="fa fa-pencil payment-inline-edit-icon" aria-hidden="true"></i>
              </button>
            </ng-container>
            <ng-template #dueDateViewReadOnly>
              {{ invoice.due_date ? (invoice.due_date | date: 'dd/MM/yyyy') : '—' }}
            </ng-template>
          </span>
        </div>

        <div class="detail-item" *ngIf="isPaymentTrackingInvoice()">
          <span class="detail-label">
            <i class="fa fa-credit-card"></i>
            {{ 'invoices_v2.payment.payment_status' | translate }}
          </span>
          <span class="detail-value detail-value--editable" (click)="$event.stopPropagation()">
            <ng-container *ngIf="canEditPayment(); else paymentStatusViewReadOnly">
              <div class="payment-inline-wrap" (click)="$event.stopPropagation()">
                <button
                  type="button"
                  class="payment-status-badge payment-status-badge--editable"
                  [class.payment-status-badge--menu-open]="showPaymentStatusMenu"
                  [ngClass]="getPaymentStatusBadgeClass()"
                  [disabled]="isSavingPaymentUpdate"
                  (click)="togglePaymentStatusMenu($event)"
                >
                  {{ getPaymentStatusLabel() || ('invoices_v2.payment.status_paid' | translate) }}
                  <i class="fa fa-caret-down" aria-hidden="true"></i>
                </button>
              </div>
            </ng-container>
            <ng-template #paymentStatusViewReadOnly>
              <span
                *ngIf="getPaymentStatusLabel() as payLabelReadOnly"
                class="payment-status-badge"
                [ngClass]="getPaymentStatusBadgeClass()"
              >
                {{ payLabelReadOnly }}
              </span>
              <span *ngIf="!getPaymentStatusLabel()">—</span>
            </ng-template>
          </span>
        </div>

        <div class="detail-item" *ngIf="!isReceipt()">
          <span class="detail-label">
            <i class="fa fa-barcode"></i>
            {{ 'invoices_v2.view.allocation_number' | translate }}
          </span>
          <span class="detail-value" [class.success]="invoice.confirmation_number">
            {{ invoice.confirmation_number || ('invoices_v2.view.none' | translate) }}
          </span>
        </div>

        <div class="detail-item" *ngIf="invoice.customer_name">
          <span class="detail-label">
            <i class="fa fa-user"></i>
            {{ 'invoices_v2.common.customer' | translate }}
          </span>
          <span class="detail-value">{{ invoice.customer_name }}</span>
        </div>

        <div class="detail-item" *ngIf="!isReceipt() && procedureFlags.isInProcedureCycle">
          <span class="detail-label">
            <i class="fa fa-gears"></i>
            {{ 'invoices_v2.procedure.cycle_label' | translate }}
          </span>
          <span class="detail-value">
            <span class="procedure-badge">
              <i class="fa fa-exclamation-triangle"></i>
              {{ 'invoices_v2.procedure.in_cycle' | translate }}
            </span>
            <span *ngIf="invoice.procedure != null" class="procedure-chosen"> — {{ getProcedureLabel() }}</span>
          </span>
        </div>

        <div class="detail-item total">
          <span class="detail-label">
            <i class="fa fa-money"></i>
            {{ 'invoices_v2.common.total_amount' | translate }}
          </span>
          <span class="detail-value">
            {{ getDisplayAmount(invoice) | number:'1.2-2' }} ₪
            <span *ngIf="isForeignCurrencyInvoice()" class="foreign-total-hint">
              {{ getForeignOriginalTotal() | number:'1.2-2' }} {{ getInvoiceCurrencyCode() }}
            </span>
          </span>
        </div>

        <div class="detail-item detail-item--exchange-rate" *ngIf="isForeignCurrencyInvoice() && getForeignCurrencyDetails() as fc">
          <span class="detail-label">
            <i class="fa fa-exchange"></i>
            {{ 'invoices_v2.currency.exchange_rate' | translate }}
          </span>
          <span class="detail-value">
            <span class="exchange-rate-value">
              1 {{ fc.currency_code }} = {{ fc.exchange_rate | number:'1.2-4' }} ₪
              <span class="detail-muted" *ngIf="fc.exchange_rate_date">({{ fc.exchange_rate_date }})</span>
              <span class="detail-muted" *ngIf="fc.exchange_rate_source"> · {{ fc.exchange_rate_source }}</span>
            </span>
          </span>
        </div>
      </div>

      <!-- Primary Actions Card -->
      <div class="actions-card" *ngIf="invoice.confirm === 0">
        <h3 class="card-title">
          <i class="fa fa-cogs"></i>
          {{ 'invoices_v2.common.actions' | translate }}
        </h3>
        
        <div class="actions-grid">
          <button 
            class="action-btn edit"
            (click)="editInvoice()"
          >
            <i class="fa fa-edit"></i>
            <span>{{ 'invoices_v2.common.edit' | translate }}</span>
          </button>

          <button 
            class="action-btn confirm"
            (click)="confirmInvoice()"
          >
            <i class="fa fa-check"></i>
            <span>{{ 'invoices_v2.common.confirm' | translate }}</span>
          </button>

          <button 
            class="action-btn delete"
            (click)="deleteInvoice()"
          >
            <i class="fa fa-trash"></i>
            <span>{{ 'invoices_v2.common.delete' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Confirmed invoice actions: allocation + merge into one tax invoice -->
      <div class="actions-card" *ngIf="invoice.confirm === 1 && (canRequestAllocationNumber || canBulkMerge)">
        <h3 class="card-title">
          <i class="fa fa-cogs"></i>
          {{ 'invoices_v2.common.actions' | translate }}
        </h3>
        <p class="card-hint" *ngIf="canBulkMerge">{{ 'invoices_v2.view.bulk_merge_hint' | translate }}</p>
        <div class="actions-grid">
          <button *ngIf="canRequestAllocationNumber" class="action-btn primary" (click)="requestAllocationNumber()">
            <i class="fa fa-plus-circle"></i>
            <span>{{ 'invoices_v2.view.request_allocation' | translate }}</span>
          </button>
          <button *ngIf="canBulkMerge" type="button" class="action-btn merge" (click)="openBulkMergeModal()">
            <i class="fa fa-compress"></i>
            <span>{{ 'invoices_v2.list.bulk_convert_button' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Procedure cycle actions -->
      <div class="actions-card tax-actions" *ngIf="procedureFlags.showProcedureEntry && invoice.confirm === 1">
        <h3 class="card-title">
          <i class="fa fa-gears"></i>
          {{ 'invoices_v2.procedure.cycle_title' | translate }}
        </h3>
        <p class="card-hint">{{ 'invoices_v2.procedure.cycle_hint' | translate }}</p>
        <div class="actions-grid">
          <button type="button" class="action-btn primary" (click)="openProcedureModal()">
            <i class="fa fa-gears"></i>
            <span>{{ 'invoices_v2.procedure.open_actions' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Additional information (tax-related actions + reverse — same eligibility as list CRUD) -->
      <div
        class="actions-card tax-actions"
        *ngIf="
          canCreateReverseInvoice() ||
          (!isReceipt() && canCancel && !canCreateReverseInvoice()) ||
          (!isReceipt() && canAfterHearingApproval) ||
          (!isReceipt() && canResendSecond && !procedureFlags.showProcedureEntry)
        "
      >
        <h3 class="card-title">
          <i class="fa fa-building"></i>
          {{ 'invoices_v2.view.additional_information' | translate }}
        </h3>
        
        <div class="actions-list">
          <button *ngIf="canCreateReverseInvoice()" type="button" class="action-btn-sm reverse" (click)="createReverseInvoice()">
            <i class="fa fa-undo"></i>
            {{ 'invoices_v2.list.create_reverse' | translate }}
          </button>

          <!-- Tax-authority cancel: hide when reverse is available so users follow POST /reverse for corrections (list parity). -->
          <button *ngIf="!isReceipt() && canCancel && !canCreateReverseInvoice()" class="action-btn-sm danger" (click)="procedureFlags.showProcedureEntry ? openProcedureModal() : cancelInvoice()">
            <i class="fa fa-times-circle"></i>
            {{ 'invoices_v2.view.cancel_invoice' | translate }}
          </button>
          
          <button *ngIf="!isReceipt() && canAfterHearingApproval" class="action-btn-sm success" (click)="openProcedureModal()">
            <i class="fa fa-gavel"></i>
            {{ 'invoices_v2.view.after_hearing' | translate }}
          </button>

          <button *ngIf="!isReceipt() && canResendSecond && !procedureFlags.showProcedureEntry" class="action-btn-sm info" (click)="openProcedureModal()">
            <i class="fa fa-paper-plane"></i>
            {{ 'invoices_v2.view.send_again' | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- Right Panel - Invoice Preview -->
    <div class="preview-panel">
      <div class="preview-header">
        <h3>
          <i class="fa fa-eye"></i>
          {{ 'invoices_v2.view.invoice_preview' | translate }}
        </h3>
        <div class="preview-actions" *ngIf="invoice.confirm === 1">
          <button
            *ngIf="canBulkMerge"
            type="button"
            class="preview-btn preview-btn--merge"
            (click)="openBulkMergeModal()"
            [title]="'invoices_v2.list.bulk_convert_button' | translate"
          >
            <i class="fa fa-compress"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.list.bulk_convert_button' | translate }}</span>
          </button>
          <button type="button" class="preview-btn" (click)="generatePdf()" [title]="'invoices_v2.view.download_pdf' | translate">
            <i class="fa fa-download"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.view.download_pdf' | translate }}</span>
          </button>
          <button
            type="button"
            class="preview-btn preview-btn--reprint"
            (click)="downloadReprintPdf()"
            [title]="'invoices_v2.view.download_reprint_pdf' | translate"
          >
            <i class="fa fa-copy"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.view.download_reprint_pdf' | translate }}</span>
          </button>
          <button type="button" class="preview-btn share-email" (click)="shareByEmail()" [title]="'invoices_v2.view.email_btn' | translate">
            <i class="fa fa-envelope"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.view.email_btn' | translate }}</span>
          </button>
          <button type="button" class="preview-btn share-whatsapp" (click)="shareByWhatsApp()" [title]="'invoices_v2.view.whatsapp' | translate">
            <i class="fa fa-whatsapp"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.view.whatsapp' | translate }}</span>
          </button>
          <button type="button" class="preview-btn copy-link" (click)="copyInvoiceLink()" [title]="'invoices_v2.view.copy_link' | translate">
            <i class="fa fa-link"></i>
            <span class="preview-btn-label">{{ 'invoices_v2.view.copy_link' | translate }}</span>
          </button>
        </div>
      </div>
      <div class="preview-content">
        <div class="invoice-frame-wrapper">
          <iframe
            *ngIf="invoiceHtmlUrl"
            [src]="invoiceHtmlUrl"
            class="invoice-iframe"
            frameborder="0"
            scrolling="auto"
          ></iframe>
          <div *ngIf="!invoiceHtmlUrl" class="loading-state">
            <div class="loader"></div>
            <span>{{ 'invoices_v2.view.loading_invoice' | translate }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="full-loading">
  <div class="loader-large"></div>
  <span>{{ 'invoices_v2.view.loading' | translate }}</span>
</div>

<!-- Bulk Merge Modal -->
<div *ngIf="showBulkMergeModal" class="email-modal-backdrop" (click)="closeBulkMergeModal()">
  <div
    class="email-modal-dialog bulk-merge-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="email-modal-header">
      <span class="email-modal-title">
        <i class="fa fa-compress"></i>
        {{ 'invoices_v2.view.bulk_merge_modal_title' | translate }}
      </span>
      <button class="email-modal-close" type="button" (click)="closeBulkMergeModal()">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Body -->
    <div class="email-modal-body">
      <!-- Current invoice (fixed) -->
      <div class="bulk-merge-current-row">
        <i class="fa fa-lock bulk-merge-lock-icon"></i>
        <span class="bulk-merge-current-label">{{ 'invoices_v2.view.bulk_merge_current' | translate }}</span>
        <span class="bulk-merge-current-number">{{ invoice.document_number || ('#' + invoiceId) }}</span>
      </div>

      <p class="email-modal-hint">{{ 'invoices_v2.view.bulk_merge_modal_hint' | translate }}</p>

      <!-- Loading -->
      <div *ngIf="isLoadingBulkMerge" class="list-loading" style="padding: 20px; text-align: center;">
        <div class="loader-small"></div>
      </div>

      <!-- Invoice list -->
      <div *ngIf="!isLoadingBulkMerge" class="bulk-merge-list">
        <!-- Empty state -->
        <div *ngIf="bulkMergeInvoicesList.length === 0" class="empty-list">
          {{ 'invoices_v2.view.bulk_merge_empty' | translate }}
        </div>

        <!-- Items -->
        <div
          *ngFor="let inv of bulkMergeInvoicesList"
          class="bulk-merge-item"
          [class.bulk-merge-item--selected]="isBulkMergeSelected(getBulkMergeRowId(inv))"
          [class.bulk-merge-item--other-type]="!isBulkMergeRowSelectable(inv)"
        >
          <input
            type="checkbox"
            class="bulk-select-checkbox"
            [checked]="isBulkMergeSelected(getBulkMergeRowId(inv))"
            [disabled]="!isBulkMergeRowSelectable(inv)"
            (change)="toggleBulkMergeSelection(getBulkMergeRowId(inv))"
          />
          <span class="bulk-merge-item__type">{{ getInvoiceTypeLabel(inv.invoice_type) }}</span>
          <span class="bulk-merge-item__number">{{ inv.document_number || ('#' + getBulkMergeRowId(inv)) }}</span>
          <span class="bulk-merge-item__date">{{ inv.invoice_date | date: 'dd/MM/yyyy' }}</span>
          <span class="bulk-merge-item__amount">{{ getDisplayAmount(inv) | number:'1.2-2' }} ₪</span>
          <button
            type="button"
            class="bulk-merge-item__preview-btn"
            (click)="$event.stopPropagation(); openInvoicePreview(getBulkMergeRowId(inv))"
            [title]="'invoices_v2.view.preview' | translate"
          >
            <i class="fa fa-eye"></i>
          </button>
          <button
            type="button"
            class="bulk-merge-item__exclude-btn"
            [disabled]="isBulkMergeExcluding(getBulkMergeRowId(inv))"
            (click)="$event.stopPropagation(); confirmExcludeFromBulkMerge(inv)"
            [title]="'invoices_v2.view.bulk_merge_exclude_btn' | translate"
          >
            <i class="fa" [ngClass]="isBulkMergeExcluding(getBulkMergeRowId(inv)) ? 'fa-spinner fa-spin' : 'fa-eye-slash'"></i>
          </button>
        </div>
      </div>

      <!-- Selected count -->
      <div *ngIf="bulkMergeSelectedIds.length > 0" class="bulk-merge-count">
        <i class="fa fa-check-circle"></i>
        {{
          curentLang === 'ar'
            ? 'محدد ' + bulkMergeSelectedIds.length + ' + الفاتورة الحالية'
            : curentLang === 'he'
              ? 'נבחרו ' + bulkMergeSelectedIds.length + ' + החשבונית הנוכחית'
              : bulkMergeSelectedIds.length + ' selected + current invoice'
        }}
      </div>
    </div>

    <!-- Footer -->
    <div class="email-modal-footer">
      <button type="button" class="email-btn-cancel" (click)="closeBulkMergeModal()">
        {{ 'invoices_v2.common.cancel' | translate }}
      </button>
      <button
        type="button"
        class="email-btn-send btn-merge-execute"
        (click)="executeBulkMerge()"
        [disabled]="bulkMergeSelectedIds.length === 0 || isLoadingBulkMerge"
      >
        <i class="fa fa-compress"></i>
        {{ 'invoices_v2.view.bulk_merge_execute' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Invoice Preview Modal (inside Bulk Merge) -->
<div *ngIf="showInvoicePreviewModal" class="email-modal-backdrop preview-modal-backdrop" (click)="closeInvoicePreview()">
  <div
    class="preview-modal-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <div class="email-modal-header">
      <span class="email-modal-title">
        <i class="fa fa-eye"></i>
        {{ 'invoices_v2.view.preview' | translate }}
        <span *ngIf="previewInvoiceNumber" class="preview-modal-number"> — {{ previewInvoiceNumber }}</span>
      </span>
      <div class="preview-modal-header-actions">
        <button
          type="button"
          class="preview-select-btn"
          *ngIf="previewInvoiceId && !isBulkMergeSelected(previewInvoiceId) && isPreviewBulkMergeSelectable()"
          (click)="toggleBulkMergeSelection(previewInvoiceId)"
        >
          <i class="fa fa-check-square-o"></i>
          {{ curentLang === 'ar' ? 'تحديد للدمج' : curentLang === 'he' ? 'בחר למיזוג' : 'Select for merge' }}
        </button>
        <button
          type="button"
          class="preview-deselect-btn"
          *ngIf="previewInvoiceId && isBulkMergeSelected(previewInvoiceId) && isPreviewBulkMergeSelectable()"
          (click)="toggleBulkMergeSelection(previewInvoiceId)"
        >
          <i class="fa fa-minus-square-o"></i>
          {{ curentLang === 'ar' ? 'إلغاء التحديد' : curentLang === 'he' ? 'בטל בחירה' : 'Deselect' }}
        </button>
        <button class="email-modal-close" type="button" (click)="closeInvoicePreview()">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
    <div class="preview-modal-body">
      <div *ngIf="isLoadingPreview" class="list-loading" style="padding: 40px; text-align: center;">
        <div class="loader-small"></div>
      </div>
      <iframe
        *ngIf="!isLoadingPreview && previewInvoiceUrl"
        [src]="previewInvoiceUrl"
        class="preview-modal-iframe"
        frameborder="0"
        scrolling="auto"
      ></iframe>
    </div>
  </div>
</div>

<!-- Email Dialog -->
<div *ngIf="showEmailDialog" class="email-modal-backdrop" (click)="onCancelEmailDialog()">
  <div
    class="email-modal-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="email-modal-header">
      <span class="email-modal-title">
        <i class="fa fa-envelope"></i>
        {{ 'invoices_v2.view.send_invoice_email' | translate }}
      </span>
      <button class="email-modal-close" type="button" (click)="onCancelEmailDialog()">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Body -->
    <div class="email-modal-body" [formGroup]="emailForm">
      <p class="email-modal-hint">
        {{ 'invoices_v2.view.no_email_saved' | translate }}
      </p>

      <div class="email-field-group">
        <label class="email-field-label">
          {{ 'invoices_v2.view.email_address' | translate }}
        </label>
        <input
          type="email"
          class="email-field-input"
          formControlName="email"
          [class.is-invalid]="emailForm.get('email')?.invalid && emailForm.get('email')?.touched"
          placeholder="example@email.com"
        />
        <div *ngIf="emailForm.get('email')?.invalid && emailForm.get('email')?.touched" class="email-field-error">
          <span *ngIf="emailForm.get('email')?.errors?.['required']">
            {{ 'invoices_v2.view.email_required' | translate }}
          </span>
          <span *ngIf="emailForm.get('email')?.errors?.['email']">
            {{ 'invoices_v2.view.email_invalid' | translate }}
          </span>
        </div>
      </div>

      <div class="email-save-row">
        <input type="checkbox" id="saveEmailPerm" formControlName="savePermanently" class="email-save-checkbox" />
        <label for="saveEmailPerm" class="email-save-label">
          <i class="fa fa-save"></i>
          {{ 'invoices_v2.view.save_email' | translate }}
        </label>
      </div>
    </div>

    <!-- Footer -->
    <div class="email-modal-footer">
      <button type="button" class="email-btn-cancel" (click)="onCancelEmailDialog()">
        {{ 'invoices_v2.common.cancel' | translate }}
      </button>
      <button
        type="button"
        class="email-btn-send"
        (click)="onSendEmailFromDialog()"
        [disabled]="emailForm.invalid"
      >
        <i class="fa fa-paper-plane"></i>
        {{ 'invoices_v2.view.send_invoice' | translate }}
      </button>
    </div>
  </div>
</div>

<div
  *ngIf="showPaymentStatusMenu"
  class="payment-status-popover"
  [ngStyle]="paymentStatusMenuStyle"
  role="menu"
  (click)="$event.stopPropagation()"
>
  <button type="button" class="payment-status-menu__item" role="menuitem" (click)="selectPaymentStatus('paid', $event)">
    {{ 'invoices_v2.payment.status_paid' | translate }}
  </button>
  <button type="button" class="payment-status-menu__item" role="menuitem" (click)="selectPaymentStatus('unpaid', $event)">
    {{ 'invoices_v2.payment.status_unpaid' | translate }}
  </button>
</div>

<div
  *ngIf="showDueDateEditor"
  #dueDatePopover
  class="payment-due-popover"
  [ngStyle]="dueDatePopoverStyle"
  role="dialog"
  [attr.aria-label]="'invoices_v2.payment.due_date' | translate"
  (click)="$event.stopPropagation()"
>
  <div class="payment-due-popover__header">
    <span class="payment-due-popover__title">{{ 'invoices_v2.payment.due_date' | translate }}</span>
    <button type="button" class="payment-due-popover__close" (click)="cancelDueDateEdit($event)" aria-label="Close">
      <i class="fa fa-times" aria-hidden="true"></i>
    </button>
  </div>
  <div class="payment-due-popover__body">
    <p-calendar
      class="p-calendar-custom payment-due-popover__calendar"
      [(ngModel)]="dueDateEditValue"
      [inline]="true"
      dateFormat="dd/mm/yy"
      [selectOtherMonths]="true"
      [showButtonBar]="true"
      [todayButtonStyleClass]="'p-button-text'"
      [clearButtonStyleClass]="'p-button-text'"
    ></p-calendar>
  </div>
  <div class="payment-due-popover__actions">
    <button type="button" class="payment-inline-btn payment-inline-btn--primary" (click)="saveDueDateInline($event)">
      {{ 'invoices_v2.payment.save_payment' | translate }}
    </button>
    <button type="button" class="payment-inline-btn" (click)="clearDueDateInline($event)">
      {{ 'invoices_v2.payment.clear_due_date' | translate }}
    </button>
    <button type="button" class="payment-inline-btn" (click)="cancelDueDateEdit($event)">
      {{ 'invoices_v2.common.cancel' | translate }}
    </button>
  </div>
</div>

<!-- Procedure cycle modal -->
<div *ngIf="showProcedureModal" class="email-modal-backdrop" (click)="closeProcedureModal()">
  <div
    class="email-modal-dialog procedure-modal-dialog"
    [dir]="curentLang === 'ar' || curentLang === 'he' ? 'rtl' : 'ltr'"
    (click)="$event.stopPropagation()"
  >
    <div class="email-modal-header">
      <span class="email-modal-title">
        <i class="fa fa-gears"></i>
        {{ 'invoices_v2.procedure.cycle_title' | translate }}
      </span>
      <button class="email-modal-close" type="button" (click)="closeProcedureModal()">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div class="email-modal-body">
      <app-invoices-v2-procedure-panel
        [invoice]="invoice"
        [invoiceId]="invoiceId"
        [curentLang]="curentLang"
        (completed)="onProcedureActionCompleted()"
        (closed)="closeProcedureModal()"
      ></app-invoices-v2-procedure-panel>
    </div>
  </div>
</div>
